/**
 * Drama-feature UI string dictionary, keyed by the global EN/中文 switch.
 *
 * Usage:
 *   const t = dramaT(lang)
 *   <h3>{t('tab.episodes')}</h3>
 *   <p>{t('episodes.allDone', readyCount)}</p>   // value may be a function
 *
 * Data fields (character names, segment summaries, …) stay on `pickLocalized`
 * over the *_i18n columns — this dictionary is only for static UI chrome.
 */
import type { DramaLang } from '@/types/drama'

type Entry = { en: string; zh: string } | { en: (...a: any[]) => string; zh: (...a: any[]) => string }

const S = {
  // ---- tabs + hints ----
  'tab.episodes': { en: 'Episodes', zh: '剧集' },
  'tab.story': { en: 'Story', zh: '剧情' },
  'tab.characters': { en: 'Characters', zh: '角色' },
  'tab.segments': { en: 'Segments', zh: '片段' },
  'tab.mixPlans': { en: 'Mix Plans', zh: '混剪方案' },
  'tab.renders': { en: 'Edit', zh: '精修' },
  'tab.outputs': { en: 'Final Cuts', zh: '成片' },
  'tabHint.episodes': { en: 'Upload source episodes and start AI analysis (segmenting, character ID, story outline).', zh: '上传短剧原片，并启动 AI 分析（切片段、识别角色、生成剧情概要）。' },
  'tabHint.story': { en: 'AI-generated whole-drama story overview; editable.', zh: 'AI 生成的全剧剧情概要，可编辑。' },
  'tabHint.characters': { en: 'AI-detected characters; rename, merge, split, or replace avatars.', zh: 'AI 识别出的角色，可改名、合并、拆分、更换头像。' },
  'tabHint.segments': { en: 'Searchable clip library cut by plot; filter by tag/character/line and preview.', zh: '按剧情切分的可检索片段库，可按标签/角色/台词筛选并预览。' },
  'tabHint.mixPlans': { en: 'AI-recommended “which clips, combined to target duration” proposals; pick one to edit.', zh: 'AI 推荐的「选哪些片段、怎么组合到目标时长」的方案，挑一个进入精修。' },
  'tabHint.renders': { en: 'Polish a proposal (transitions/speed/captions/BGM/tail card) and export the final cut.', zh: '对选片方案做转场/变速/字幕/BGM/片尾卡等精修，并导出成片。' },
  'tabHint.outputs': { en: 'Exported final cuts; preview and download here.', zh: '已导出的成片，可在线预览和下载。' },

  // ---- episodes ----
  'episodes.title': { en: 'Episodes', zh: 'Episodes' },
  'episodes.empty': { en: 'No episodes yet. Use “Add episode” below to upload.', zh: '还没有剧集。使用下方的「添加新剧集」上传。' },
  'episodes.pendingReady': {
    en: (pending: number, ready: number) => `${pending} pending · ready to analyze.${ready > 0 ? ` ${ready} already done.` : ''}`,
    zh: (pending: number, ready: number) => `${pending} 集待处理 · 可以开始分析。${ready > 0 ? ` 已有 ${ready} 集分析完成。` : ''}`,
  },
  'episodes.reorderHint': { en: ' Drag cards to reorder before starting.', zh: ' 开始前可拖拽卡片调整顺序。' },
  'episodes.processing': { en: (n: number) => `AI analysis running — ${n} episode(s) processing.`, zh: (n: number) => `AI 分析进行中 —— ${n} 集正在处理。` },
  'episodes.allDone': { en: (n: number) => `All ${n} episodes analyzed. Head to Story / Mix Plans to start mixing.`, zh: (n: number) => `全部 ${n} 集已分析完成。前往 Story / Mix Plans 开始混剪。` },
  'episodes.startAnalysis': { en: (n: number) => `Start AI analysis (${n})`, zh: (n: number) => `开始 AI 分析 (${n})` },
  'episodes.starting': { en: 'Starting…', zh: '启动中…' },
  'episodes.analyzing': { en: 'Analyzing…', zh: '分析中…' },
  'episodes.preview': { en: 'Preview', zh: '预览' },
  'episodes.collapse': { en: 'Collapse', zh: '收起' },
  'episodes.reanalyze': { en: 'Re-analyze', zh: '重新分析' },
  'episodes.delete': { en: 'Delete', zh: '删除' },
  'episodes.addNew': { en: 'Add episode', zh: '添加新剧集' },
  'episodes.deleteConfirm': { en: (n: number, name: string) => `Delete episode ${n}${name ? ` (${name})` : ''}? The uploaded file will be removed.`, zh: (n: number, name: string) => `删除第 ${n} 集${name ? `（${name}）` : ''}？已上传的文件将被移除。` },

  // ---- segments (filters + preview) ----
  'seg.filters': { en: 'Filters', zh: '筛选' },
  'seg.clear': { en: 'Clear', zh: '清除' },
  'seg.reset': { en: 'Reset', zh: '重置' },
  'seg.episodes': { en: 'Episodes', zh: '剧集' },
  'seg.all': { en: 'All', zh: '全部' },
  'seg.plotTags': { en: 'Plot tags', zh: '剧情标签' },
  'seg.emotionTags': { en: 'Emotion tags', zh: '情绪标签' },
  'seg.characters': { en: 'Characters', zh: '角色' },
  'seg.minHook': { en: 'Min hook score (0-10)', zh: '最低钩子分（0-10）' },
  'seg.scoreHint': {
    en: '🪝 Hook score: how gripping/attention-grabbing this clip is. ⚡ Conflict score: intensity of drama/tension. Both 0–10 (AI-rated) — higher = better highlight material.',
    zh: '🪝 钩子分：这个片段抓人/吸引力的强度。⚡ 冲突分：戏剧冲突/张力的强度。均为 0–10（AI 评分），分越高越适合做高光。',
  },
  'seg.textSearch': { en: 'Text search (summary / subtitle)', zh: '文本搜索（摘要 / 字幕）' },
  'seg.search': { en: 'Search', zh: '搜索' },
  'seg.searching': { en: 'Searching…', zh: '搜索中…' },
  'seg.count': { en: (n: number) => `${n} segments`, zh: (n: number) => `${n} 个片段` },
  'seg.loadedOf': { en: (n: number) => ` / ${n} loaded`, zh: (n: number) => ` / 共加载 ${n} 个` },
  'seg.filtered': { en: ' (filtered)', zh: '（已筛选）' },
  'seg.visualAnalysis': { en: 'Visual analysis (Gemini)', zh: '视觉分析（Gemini）' },
  'seg.unsafe': { en: '⚠️ Not ad-safe', zh: '⚠️ 不适合投放广告' },

  // ---- mix plans (proposals) ----
  'plans.generateNew': { en: 'Generate selection proposals', zh: '生成选片方案' },
  'plans.generateMore': { en: 'Generate another batch of proposals', zh: '再生成一批选片方案' },
  'plans.collapse': { en: 'Collapse', zh: '收起' },
  'plans.emptyTitle': { en: 'No proposals yet. Describe an angle above and generate several, then pick one to edit.', zh: '还没有选片方案。在上方描述切入角度，生成多个方案再挑一个进入精修。' },
  'plans.segs': { en: (n: number) => `${n} clips`, zh: (n: number) => `${n} 片段` },
  'plans.untitled': { en: 'Untitled proposal', zh: '未命名方案' },
  'plans.approx': { en: (d: number, target?: number | null) => `~${d}s${target ? ` / target ${target}s` : ''}`, zh: (d: number, target?: number | null) => `约 ${d}s${target ? ` / 目标 ${target}s` : ''}` },
  'plans.view': { en: 'View', zh: '查看' },
  'plans.edit': { en: 'Edit', zh: '精修' },
  'plans.deleteConfirm': { en: (code: string) => `Delete proposal ${code}? Renders derived from it (if any) will also be removed.`, zh: (code: string) => `删除方案 ${code}？由它派生的 Render（如有）也会一并删除。` },
  'plans.tailPlaceholder': { en: '(tail-card placeholder)', zh: '（尾帖占位）' },
  'plans.previewOpen': { en: 'Preview', zh: '预览' },
  'plans.previewClose': { en: 'Collapse', zh: '收起' },

  // ---- render workbench (精修) ----
  'render.empty': { en: 'No edit sessions yet. Open the Mix Plans tab and click “Edit” on a proposal.', zh: '还没有精修任务。打开 Mix Plans 标签页，挑一个方案点「精修此方案」即可进入工作台。' },
  'render.plan': { en: (code: string) => `plan ${code}`, zh: (code: string) => `方案 ${code}` },
  'render.clips': { en: (n: number) => `${n} clips`, zh: (n: number) => `${n} 片段` },
  'render.saving': { en: 'Saving…', zh: '保存中…' },
  'render.savedAt': { en: 'Saved', zh: '已保存' },
  'render.save': { en: 'Save', zh: '保存' },
  'render.export': { en: 'Export', zh: '导出成片' },
  'render.exporting': { en: 'Exporting…', zh: '导出中…' },
  'render.exported': { en: 'Exported', zh: '已导出成片' },
  'render.download': { en: 'Download', zh: '下载' },
  'render.bgm': { en: 'Background music', zh: '背景音乐' },
  'render.bgmMood': { en: 'BGM mood (built-in library)', zh: 'BGM 情绪（内置曲库）' },
  'render.volume': { en: (v: string) => `Volume (${v})`, zh: (v: string) => `音量（${v}）` },
  'render.bgmUpload': { en: 'Upload BGM', zh: '上传 BGM' },
  'render.bgmUploading': { en: 'Uploading…', zh: '上传中…' },
  'render.bgmCustom': { en: (name: string) => `Custom: ${name}`, zh: (name: string) => `自定义：${name}` },
  'render.bgmCustomHint': { en: 'A custom track overrides the mood library.', zh: '自定义曲目优先于情绪曲库。' },
  'render.bgmClearCustom': { en: 'Use library instead', zh: '改用情绪曲库' },
  'render.intro': { en: 'Intro clip (pre-roll)', zh: '前贴（片头）' },
  'render.outro': { en: 'Outro clip (post-roll)', zh: '后贴（片尾）' },
  'render.importFromLibrary': { en: 'Import from Library', zh: '从素材库选择' },
  'render.change': { en: 'Change', zh: '更换' },
  'render.remove': { en: 'Remove', zh: '移除' },
  'render.none': { en: 'None', zh: '未设置' },
  'render.pickIntro': { en: 'Select an intro clip', zh: '选择前贴视频' },
  'render.pickOutro': { en: 'Select an outro clip', zh: '选择后贴视频' },
  'render.inspectorTitle': { en: 'Segment editing', zh: '片段精修' },
  'render.order': { en: (n: number) => `order ${n}`, zh: (n: number) => `顺序 ${n}` },
  'render.purpose': { en: (p: string) => `purpose: ${p}`, zh: (p: string) => `用途：${p}` },
  'render.transition': { en: 'Transition', zh: '转场' },
  'render.speed': { en: (v: string) => `Playback speed (${v}×)`, zh: (v: string) => `播放速度（${v}×）` },
  'render.speedHint': { en: 'Mid-section defaults to 1.2× (max 1.5×); opening / closing highlights stay 1.0×.', zh: '中段默认 1.2×（最高 1.5×）；开场 / 结尾爆点保持 1.0×。' },
  'render.sfx': { en: 'Sound effect', zh: '音效' },
  'render.inOut': { en: 'Trim this clip — keep from In to Out (drag handles or type seconds)', zh: '裁剪本片段 —— 保留「入点 → 出点」之间的部分（拖动滑块或直接输入秒数）' },
  'render.captionStyle': { en: 'Caption style ▾', zh: '字幕样式 ▾' },
  'render.captionStyleHint': { en: 'Style applies to all clip captions in this render.', zh: '样式应用到本成片所有片段字幕。' },
  'render.capColor': { en: 'Color', zh: '字体色' },
  'render.capOutline': { en: 'Outline', zh: '描边色' },
  'render.capOutlineW': { en: 'Outline', zh: '描边' },
  'render.capSize': { en: 'Size', zh: '字号' },
  'render.capPosition': { en: 'Position', zh: '位置' },
  'render.capBottom': { en: 'Bottom', zh: '底部' },
  'render.capCenter': { en: 'Center', zh: '居中' },
  'render.capTop': { en: 'Top', zh: '顶部' },
  'render.capSample': { en: 'Caption preview', zh: '字幕预览效果' },
  'render.caption': { en: 'Caption', zh: '字幕' },
  'render.captionPlaceholder': { en: 'Optional overlay text', zh: '可选的叠加文字' },
  'render.selectClipHint': { en: 'Select a clip on the timeline to edit it.', zh: '在时间线中选择一个片段以进行精修。' },

  // ---- generate mix form ----
  'gen.briefLabel': { en: 'Your preferences (optional)', zh: '需求 / 偏好（可选）' },
  'gen.aiBtn': { en: 'AI: draft a direction', zh: 'AI 拟一个方向' },
  'gen.aiBtnTitle': { en: 'Optional: let AI draft one direction into the box above. The button below actually generates the creative plans.', zh: '可选：让 AI 拟一个方向填到上方文本框。真正生成创意方案请点下方按钮。' },
  'gen.briefGenerating': { en: 'Generating a brief from the story and clips…', zh: '正在根据剧情和片段生成简报…' },
  'gen.briefPlaceholder': { en: 'Optional — describe your needs / preferences, e.g. lean into face-slap reversals, more lead-couple scenes…', zh: '可选 —— 写下你的需求或偏好，例如：主打打脸逆袭、多一些男女主对手戏…' },
  'gen.briefHelp': { en: 'Optional preferences used as a reference for ALL generated creative plans. Leave blank to let AI freely cover different angles.', zh: '可选：作为生成所有创意方案的参考偏好。留空则由 AI 自由发挥、覆盖不同角度。' },
  'gen.durationRange': { en: 'Duration range (sec)', zh: '时长区间（秒）' },
  'gen.to': { en: 'to', zh: '至' },
  'gen.durationHint': { en: 'SOP long mix: 180-360s. The 15s only refers to the opening voiceover.', zh: 'SOP 长混剪：180-360 秒。15 秒仅指开场旁白。' },
  'gen.episodeRange': { en: 'Main episode range (primary plot)', zh: '主线集数范围（主要剧情）' },
  'gen.episodeRangeHint': { en: 'First N episodes = main plot / backstory; later episodes feed highlight supplements.', zh: '前 N 集 = 主线 / 背景；之后的集数用作爆点补充。' },
  'gen.count': { en: 'Creative plans to generate', zh: '生成几个创意方案' },
  'gen.countN': { en: (n: number) => `${n}`, zh: (n: number) => `${n} 个` },
  'gen.generating': { en: 'Generating…', zh: '生成中…' },
  'gen.generate': { en: (n: number) => `AI: generate ${n} mix plan${n > 1 ? 's' : ''}`, zh: (n: number) => `AI 生成 ${n} 个混剪方案` },
  'gen.footer': { en: 'A proposal only decides “which clips, combined to target duration”. Pick one and enter the Edit workbench for transitions / speed / captions / BGM / voiceover / tail card, then export.', zh: '方案只负责「选哪些片段、怎么组合到目标时长」。挑一个方案进入「精修工作台」再做转场 / 变速 / 字幕 / BGM / 旁白 / 片尾卡并导出成片。' },

  // ---- story outline ----
  'story.empty': { en: 'The story overview appears after preprocessing.', zh: '预处理完成后将显示剧情梗概。' },
  'story.title': { en: 'Story Overview', zh: '剧情梗概' },
  'story.edit': { en: 'Edit', zh: '编辑' },
  'story.regenerate': { en: 'Regenerate', zh: '重新生成' },
  'story.singleLangHint': { en: 'This overview is legacy single-language. Click “Regenerate” for a bilingual version.', zh: '这条梗概是旧版单语内容。点击「重新生成」可生成中英双语版本。' },
  'story.overviewPlaceholder': { en: 'English overview…', zh: '中文剧情梗概…' },
  'story.cancel': { en: 'Cancel', zh: '取消' },
  'story.saving': { en: 'Saving…', zh: '保存中…' },
  'story.save': { en: 'Save', zh: '保存' },
  'story.noOverview': { en: '(No overview yet)', zh: '（暂无概述）' },

  // ---- characters ----
  'char.empty': { en: 'Characters appear after preprocessing.', zh: '预处理完成后将显示角色。' },
  'char.detected': { en: (n: number) => `${n} characters detected`, zh: (n: number) => `检测到 ${n} 个角色` },
  'char.merge': { en: 'Merge', zh: '合并' },
  'char.changeAvatar': { en: 'Replace avatar image', zh: '更换头像图片' },
  'char.editNameAliases': { en: 'Edit name & aliases', zh: '编辑名称和别名' },
  'char.mergeInto': { en: 'Merge into another character', zh: '合并到另一个角色' },
  'char.splitInto': { en: 'Split into two characters', zh: '拆分为两个角色' },
  'char.lock': { en: 'Lock to avoid being overwritten by AI regeneration', zh: '锁定，避免被 AI 重新生成覆盖' },
  'char.notReal': { en: 'Not a real character', zh: '非真实角色' },
  'char.notRealConfirm': { en: (name: string) => `Mark “${name}” as not a real character?`, zh: (name: string) => `将「${name}」标记为非真实角色？` },
  'char.mergeTitle': { en: 'Merge characters', zh: '合并角色' },
  'char.mergeDesc': { en: "All of the source character's appearances move to the target. The source becomes a redirect (reversible).", zh: '来源角色的所有出场都会转移到目标角色。来源角色将变为重定向（可恢复）。' },
  'char.source': { en: 'Source (will be merged away)', zh: '来源（将被合并掉）' },
  'char.target': { en: 'Target (absorbs the source)', zh: '目标（将吸收来源）' },
  'char.choose': { en: 'Choose…', zh: '请选择…' },
  'char.sourceShort': { en: 'Source', zh: '来源' },
  'char.targetShort': { en: 'Target', zh: '目标' },
  'char.cancel': { en: 'Cancel', zh: '取消' },
  'char.notSelected': { en: 'not selected', zh: '未选择' },
  'char.splitTitle': { en: (code: string) => `Split ${code} into two characters`, zh: (code: string) => `将 ${code} 拆分为两个角色` },
  'char.splitDesc': { en: (code: string) => `Creates ${code}a and ${code}b and soft-deletes the original. You can reassign appearances to A or B later in the Segments tab.`, zh: (code: string) => `会创建 ${code}a 和 ${code}b，并软删除原角色。之后你可以在 Segments 标签页中将出场重新分配给 A 或 B。` },
  'char.split': { en: 'Split', zh: '拆分' },
  'char.editTitle': { en: 'Edit character', zh: '编辑角色' },
  'char.editDesc': { en: 'Update display name and aliases. AI regeneration respects locked characters.', zh: '更新显示名称和别名。AI 重新生成时会尊重被锁定的角色。' },
  'char.avatar': { en: 'Avatar image', zh: '头像图片' },
  'char.changeAvatarBtn': { en: 'Change avatar', zh: '更换头像' },
  'char.avatarHint': { en: 'Height is normalized on upload to keep the gallery consistent.', zh: '上传后高度自动归一，保持画廊一致。' },
  'char.displayName': { en: 'Display name', zh: '显示名称' },
  'char.aliases': { en: 'Aliases (comma-separated)', zh: '别名（逗号分隔）' },
  'char.aliasesPlaceholder': { en: 'alias1, alias2', zh: '别名1, 别名2' },
  'char.save': { en: 'Save', zh: '保存' },

  // ---- episodes (extra: add form + card a11y) ----
  'episodes.gridEmpty': { en: 'No episodes yet.', zh: '还没有剧集。' },
  'episodes.cancel': { en: 'Cancel', zh: '取消' },
  'episodes.epNum': { en: 'Episode #', zh: '集数 #' },
  'episodes.mp4File': { en: 'mp4 file', zh: 'mp4 文件' },
  'episodes.uploading': { en: 'Uploading…', zh: '上传中…' },
  'episodes.upload': { en: 'Upload', zh: '上传' },
  'episodes.uploadingPct': { en: (p: number) => `Uploading… ${p}%`, zh: (p: number) => `上传中… ${p}%` },
  'episodes.dragReorder': { en: 'Drag to reorder', zh: '拖拽以重新排序' },
  'episodes.reanalyzeTitle': { en: 'Clear this episode’s segments and re-cut with the latest logic', zh: '清除该集片段并按最新逻辑重新切分' },
  'episodes.deleteAria': { en: 'Delete episode', zh: '删除剧集' },
  'episodes.reanalyzeConfirm': { en: (n: number) => `Re-analyze episode ${n}? Its existing segments are cleared and re-cut with the latest logic (other episodes unaffected).`, zh: (n: number) => `重新分析第 ${n} 集？会清除该集现有片段并按最新逻辑重新切分（其它集不受影响）。` },

  // ---- caption track ----

  // ---- segment replace panel ----
  'srp.title': { en: 'Replace / Insert', zh: '替换 / 插入片段' },
  'srp.fullEpisode': { en: 'Full episode', zh: '整集' },
  'seg.fullEpisodes': { en: 'Full episodes (insert & trim a whole episode)', zh: '完整剧集（可整集插入再裁剪）' },
  'srp.currentPurpose': { en: 'Current purpose:', zh: '当前用途：' },
  'srp.allPurposes': { en: 'All purposes', zh: '全部用途' },
  'srp.candidatesToggle': { en: (n: number) => `Candidate clips (${n})`, zh: (n: number) => `候选片段（${n}）` },
  // SOP purpose labels (used in the purpose filter dropdown)
  'purpose.opening_highlight': { en: 'Opening highlight', zh: '开场爆点' },
  'purpose.voiceover_setup': { en: 'Voiceover setup', zh: '旁白铺垫' },
  'purpose.reverse_context': { en: 'Reverse context', zh: '倒叙背景' },
  'purpose.plot_progression': { en: 'Plot progression', zh: '剧情推进' },
  'purpose.emotional_peak': { en: 'Emotional peak', zh: '情绪高潮' },
  'purpose.final_highlight': { en: 'Final highlight', zh: '结尾爆点' },
  'purpose.tail_card': { en: 'Tail card', zh: '片尾卡' },
  'srp.searchPlaceholder': { en: 'Search summary / subtitle…', zh: '搜索摘要 / 字幕…' },
  'srp.candidates': { en: (n: number) => `${n} candidate clips`, zh: (n: number) => `${n} 个候选片段` },
  'srp.noSummary': { en: '(no summary)', zh: '（无摘要）' },
  'srp.noMatch': { en: 'No matching clips.', zh: '没有匹配的片段。' },

  // ---- transition / sfx / bgm option labels ----
  'trans.hard_cut': { en: 'Hard cut', zh: '硬切' },
  'trans.dissolve': { en: 'Dissolve', zh: '叠化' },
  'trans.flash_white': { en: 'Flash white', zh: '闪白' },
  'trans.flash_black': { en: 'Flash black', zh: '闪黑' },
  'sfx.none': { en: 'None', zh: '无' },
  'sfx.flashback': { en: 'Flashback', zh: '闪回' },
  'bgm.none': { en: '—', zh: '—' },
  'bgm.tense': { en: 'Tense', zh: '紧张' },
  'bgm.romantic': { en: 'Romantic', zh: '暧昧' },
  'bgm.warm': { en: 'Warm', zh: '温馨' },
  'bgm.low_turning_point': { en: 'Low / turning point', zh: '低谷转折' },
  'bgm.conflict': { en: 'Conflict', zh: '矛盾' },
  'bgm.sneaky': { en: 'Sneaky', zh: '偷偷摸摸' },

  // ---- BGM track library (曲目库) ----
  'bgm.choose': { en: 'Pick track', zh: '选择曲目' },
  'bgm.libraryTitle': { en: 'My BGM library', zh: '我的背景音乐曲库' },
  'bgm.libraryDesc': {
    en: 'Your personal track library. Browse, audition and pick a track, or upload your own — your uploads follow you across all projects.',
    zh: '你的个性化曲目库。浏览、试听并选择曲目，或上传你自己的曲目——上传的曲目在你的所有项目中通用。',
  },
  'bgm.filterAll': { en: 'All', zh: '全部' },
  'bgm.untagged': { en: 'Untagged', zh: '未标记' },
  'bgm.builtin': { en: 'Library', zh: '曲库' },
  'bgm.uploaded': { en: 'My upload', zh: '我的上传' },
  'bgm.audition': { en: 'Audition', zh: '试听' },
  'bgm.select': { en: 'Select', zh: '选用' },
  'bgm.selected': { en: 'Selected', zh: '已选用' },
  'bgm.empty': { en: 'No tracks in this category.', zh: '该分类下暂无曲目。' },
  'bgm.uploadTrack': { en: 'Upload track', zh: '上传曲目' },
  'bgm.uploadTags': { en: 'Tags (optional)', zh: '标签（可选）' },
  'bgm.uploadTagsPlaceholder': { en: 'comma,separated,tags', zh: '逗号,分隔,多个标签' },
  'bgm.uploadDone': { en: 'Track uploaded', zh: '曲目已上传' },
  'bgm.auditionFailed': { en: 'Failed to play track', zh: '试听失败' },
  'bgm.addTag': { en: 'Tag', zh: '标签' },
  'bgm.newTag': { en: 'new tag…', zh: '新标签…' },
  'bgm.delete': { en: 'Remove from library', zh: '从曲库移除' },
  'bgm.deleted': { en: 'Track removed', zh: '曲目已移除' },
  'bgm.editFailed': { en: 'Edit failed', zh: '编辑失败' },
  // ---- Part 3: shared render params + apply scope ----
  'render.sharedTitle': { en: 'Shared settings', zh: '统一设置' },
  'render.sharedHint': {
    en: 'Apply a value to the whole film or selected clips. Per-clip tweaks below still override.',
    zh: '可一次应用到全片或选定片段；下方逐片段的微调仍会覆盖。',
  },
  'render.scopeWhole': { en: 'Whole film', zh: '全片' },
  'render.scopeClips': { en: 'Selected clips', zh: '选定片段' },
  'render.applyTo': { en: 'Apply to', zh: '应用范围' },
  'render.bulkSpeed': { en: 'Playback speed', zh: '播放速度' },
  'render.bulkTransition': { en: 'Transition', zh: '转场' },
  'render.apply': { en: 'Apply', zh: '应用' },
  'render.applied': { en: 'Applied', zh: '已应用' },
  'render.pickClips': { en: 'Pick clips', zh: '选择片段' },
  'render.clipsSelected': { en: (n: number) => `${n} clips`, zh: (n: number) => `${n} 个片段` },
  'render.bgmScope': { en: 'BGM range', zh: 'BGM 范围' },

  // ---- project list + create dialog ----
  'proj.subtitle': { en: 'Upload episodes, let AI analyze them, then auto-cut vertical short-video ads.', zh: '上传剧集，让 AI 自动分析，再自动剪出竖屏短视频广告。' },
  'proj.new': { en: 'New project', zh: '新建项目' },
  'proj.empty': { en: 'No drama projects yet. Click “New project” to start.', zh: '还没有任何短剧项目。点击「新建项目」开始。' },
  'proj.updatedAt': { en: (d: string) => `Updated ${d}`, zh: (d: string) => `更新于 ${d}` },
  'proj.createTitle': { en: 'Create a drama project', zh: '新建短剧项目' },
  'proj.createDesc': { en: 'Three ways to add episodes: Add files (pick mp4s one by one); Add folder (opens the system folder picker — click again for more folders); or drag any files/folders onto the list (multiple folders at once). Subfolders are read recursively, files within each folder import in natural filename order; the first item is episode 1.', zh: '有三种方式添加剧集视频：添加文件可逐个选择 mp4；添加文件夹会打开系统的文件夹选择器导入其中的视频，要加多个文件夹再点一次即可；也可以把任意文件和文件夹组合拖拽到列表中（一次可拖入多个文件夹）。子文件夹会被递归读取，每个文件夹内的文件按文件名自然顺序导入；列表中的第一项即为第 1 集。' },
  'proj.title': { en: 'Title', zh: '标题' },
  'proj.language': { en: 'Language', zh: '语言' },
  'proj.episodesN': { en: (n: number) => `Episodes (${n})`, zh: (n: number) => `剧集（${n}）` },
  'proj.addFiles': { en: 'Add files', zh: '添加文件' },
  'proj.addFolder': { en: 'Add folder', zh: '添加文件夹' },
  'proj.addFolderTitle': { en: 'Pick a folder to import its videos. Click again to add more folders.', zh: '选择一个文件夹导入其中的视频。要再加文件夹，再点一次即可。' },
  'proj.dropHint': { en: 'Click “Add files” / “Add folder”, or drag videos or folders here.', zh: '点击「添加文件」/「添加文件夹」，或将视频或文件夹拖拽到此处。' },
  'proj.dropHint2': { en: 'Drag multiple folders at once. Add folder imports one folder per click. Files in each folder are sorted naturally (EP01 → EP02 → … → EP10) then numbered.', zh: '拖拽可一次性放入多个文件夹。添加文件夹每次导入一个文件夹，要加多个再点一次即可。每个文件夹内的文件会先按文件名自然排序（EP01 → EP02 → … → EP10）再依次编号。' },
  'proj.needMore': { en: 'Need more episodes? Add folder imports another folder (click again for more); you can also keep dragging files onto the list.', zh: '还需要更多剧集？添加文件夹可再导入一个文件夹（要加多个就多点几次），也可以继续拖拽文件到列表中。' },
  'proj.addMoreFolders': { en: 'Add more folders', zh: '添加更多文件夹' },
  'proj.uploadedOf': { en: (a: number, b: number) => `Uploaded ${a}/${b}`, zh: (a: number, b: number) => `已上传 ${a}/${b}` },
  'proj.blockersPrefix': { en: (s: string) => `Before submitting: ${s}`, zh: (s: string) => `提交前请先：${s}` },
  'proj.needTitle': { en: 'enter a project title', zh: '请输入项目标题' },
  'proj.needFile': { en: 'add at least one mp4', zh: '请至少添加一个 mp4 文件' },
  'proj.cancel': { en: 'Cancel', zh: '取消' },
  'proj.uploadingN': { en: (a: number, b: number) => `Uploading ${a}/${b}…`, zh: (a: number, b: number) => `上传中 ${a}/${b}…` },
  'proj.createAndUpload': { en: (n: string) => `Create and upload ${n} episodes`.trim(), zh: (n: string) => `创建并上传 ${n} 集`.trim() },
  'proj.noVideosTitle': { en: 'No videos found', zh: '未找到视频' },
  'proj.noVideosInFolder': { en: 'The selected folder/files contain no video clips.', zh: '所选文件夹/文件中没有任何视频片段。' },
  'proj.nothingSelected': { en: 'Nothing selected.', zh: '没有选择任何内容。' },
  'proj.filesLabel': { en: '(files)', zh: '(文件)' },
  'proj.addedN': { en: (n: number) => `Added ${n} videos`, zh: (n: number) => `已添加 ${n} 个视频` },
  'proj.skippedN': { en: (n: number) => ` · skipped ${n} non-video files`, zh: (n: number) => ` · 已跳过 ${n} 个非视频文件` },
  'proj.genericError': { en: 'Something went wrong, please retry', zh: '出错了，请重试' },
  'proj.created': { en: 'Project created', zh: '项目已创建' },
  'proj.createdDesc': { en: (n: number) => `Uploaded ${n} episodes. Review, then click “Start AI analysis”.`, zh: (n: number) => `已上传 ${n} 集。检查无误后，点击「Start AI analysis」开始分析。` },
  'proj.moveUp': { en: 'Move up', zh: '上移' },
  'proj.moveDown': { en: 'Move down', zh: '下移' },
  'proj.removeItem': { en: 'Remove', zh: '移除' },

  // ---- delete project dialog ----
  'del.titleConfirm': { en: (title: string) => `Permanently delete "${title}"?`, zh: (title: string) => `永久删除「${title}」？` },
  'del.cannotUndo': { en: 'This cannot be undone. The project and its source files will be removed — but final cuts saved to the Asset Library are kept.', zh: '此操作无法撤销。项目及其源文件将被清除——但已保存到素材库的成片会保留。' },
  'del.willDelete': { en: 'The following will be permanently deleted:', zh: '以下内容将被永久删除：' },
  'del.episodes': { en: (n: number, mb: string) => `${n} episodes (${mb} MB)`, zh: (n: number, mb: string) => `${n} 集（${mb} MB）` },
  'del.segments': { en: (n: number) => `${n} segments`, zh: (n: number) => `${n} 个片段` },
  'del.characters': { en: (n: number) => `${n} character records`, zh: (n: number) => `${n} 条角色记录` },
  'del.plans': { en: (n: number) => `${n} edit plans`, zh: (n: number) => `${n} 个剪辑方案` },
  'del.renders': { en: (n: number) => `${n} renders`, zh: (n: number) => `${n} 个渲染成片` },
  'del.assets': { en: (n: number) => `${n} assets in the library`, zh: (n: number) => `素材库中的 ${n} 个素材` },
  'del.keptAssets': { en: (n: number) => `${n} final cut(s) in the Asset Library will be kept`, zh: (n: number) => `素材库中的 ${n} 个成片将保留` },
  'del.confirmPrefix': { en: 'Type ', zh: '输入 ' },
  'del.confirmSuffix': { en: ' to confirm', zh: ' 以确认' },
  'del.cancel': { en: 'Cancel', zh: '取消' },
  'del.deleting': { en: 'Deleting…', zh: '删除中…' },
  'del.delete': { en: 'Delete permanently', zh: '永久删除' },

  // ---- toasts ----
  'toast.avatarUpdated': { en: 'Avatar updated', zh: '头像已更新' },
  'toast.avatarUploadFailed': { en: 'Avatar upload failed', zh: '头像上传失败' },
  'toast.merged': { en: 'Merged', zh: '已合并' },
  'toast.mergeFailed': { en: 'Merge failed', zh: '合并失败' },
  'toast.charUpdated': { en: 'Character updated', zh: '角色已更新' },
  'toast.updateFailed': { en: 'Update failed', zh: '更新失败' },
  'toast.charRemoved': { en: 'Character removed', zh: '角色已移除' },
  'toast.charSplit': { en: 'Character split', zh: '角色已拆分' },
  'toast.charSplitDesc': { en: (code: string) => `${code} → 2 new characters`, zh: (code: string) => `${code} → 2 个新角色` },
  'toast.splitFailed': { en: 'Split failed', zh: '拆分失败' },
  'toast.saveFailed': { en: 'Save failed', zh: '保存失败' },
  'toast.exportDone': { en: 'Export complete', zh: '导出完成' },
  'toast.exportStarted': { en: 'Exporting… the page refreshes automatically when it finishes.', zh: '开始导出…完成后页面会自动刷新。' },
  'toast.exportFailed': { en: 'Export failed', zh: '导出失败' },
  'toast.planDeleted': { en: 'Proposal deleted', zh: '方案已删除' },
  'toast.deleteFailed': { en: 'Delete failed', zh: '删除失败' },
  'toast.draftCreated': { en: 'Edit draft created', zh: '已创建精修草稿' },
  'toast.createFailed': { en: 'Create failed', zh: '创建失败' },
  'toast.epUploaded': { en: 'Episode uploaded', zh: '剧集已上传' },
  'toast.epUploadedDesc': { en: (mb: number, n: number) => `${mb} MB → episode ${n}.`, zh: (mb: number, n: number) => `${mb} MB → 第 ${n} 集。` },
  'toast.uploadFailed': { en: 'Upload failed', zh: '上传失败' },
  'toast.reorderFailed': { en: 'Reorder failed', zh: '排序失败' },
  'toast.epReset': { en: (n: number) => `Episode ${n} reset`, zh: (n: number) => `第 ${n} 集已重置` },
  'toast.epResetDesc': { en: 'Old segments cleared and marked pending. Click “Start AI analysis” to re-cut with the latest logic.', zh: '已清除旧片段并标记为待处理，点击「Start AI analysis」即可按最新逻辑重新切分。' },
  'toast.resetFailed': { en: 'Reset failed', zh: '重置失败' },
  'toast.analysisStarted': { en: 'AI analysis started', zh: 'AI 分析已启动' },
  'toast.analysisStartedDesc': { en: (n: number) => `${n} episodes queued; takes a few minutes.`, zh: (n: number) => `已加入队列 ${n} 集，预计需要几分钟。` },
  'toast.startFailed': { en: 'Start failed', zh: '启动失败' },
  'toast.outlineSaved': { en: 'Overview saved', zh: '梗概已保存' },
  'toast.outlineSavedDesc': { en: 'Marked as user-edited; AI regeneration will skip it.', zh: '已标记为用户编辑，AI 重新生成时会跳过。' },
  'toast.regenerating': { en: 'Regenerating', zh: '正在重新生成' },
  'toast.regeneratingDesc': { en: 'AI will rebuild the story overview (bilingual).', zh: 'AI 将重建剧情梗概（中英双语）。' },
  'toast.regenFailed': { en: 'Cannot regenerate', zh: '无法重新生成' },
  'toast.briefFailed': { en: 'Brief generation failed', zh: '简报生成失败' },
  'toast.plansGenerated': { en: 'Proposals generated', zh: '方案已生成' },
  'toast.plansGeneratedDesc': { en: (n: number) => `Generated ${n} selection proposals`, zh: (n: number) => `生成了 ${n} 个选片方案` },
  'toast.genFailed': { en: 'Generation failed', zh: '生成失败' },
  'toast.bgmUploadFailed': { en: 'BGM upload failed', zh: 'BGM 上传失败' },

  // ---- preprocess progress panel ----
  'prog.done': { en: 'AI analysis complete', zh: 'AI 分析完成' },
  'prog.collapse': { en: 'Collapse', zh: '收起' },
  'prog.expand': { en: 'Expand', zh: '展开' },
  'prog.parallel': { en: (n: number) => `Analyzing ${n} episodes in parallel`, zh: (n: number) => `正在并行分析 ${n} 集` },
  'prog.doneOfTotal': { en: (d: number, total: number) => ` · ${d} / ${total} done`, zh: (d: number, total: number) => ` · 已完成 ${d} / ${total}` },
  'prog.epStage': { en: (ep: string, total: number, stage: string) => `Episode ${ep} / ${total} — ${stage}`, zh: (ep: string, total: number, stage: string) => `第 ${ep} / ${total} 集 —— ${stage}` },
  'prog.analyzingFirst': { en: (total: number) => `Analyzing episode 1 of ${total}`, zh: (total: number) => `正在分析 ${total} 集中的第 1 集` },
  'prog.finishing': { en: (stage: string) => `Finishing: ${stage}`, zh: (stage: string) => `收尾中：${stage}` },
  'prog.queued': { en: 'Queued — analysis starting…', zh: '已排队 —— 分析即将开始…' },
  'prog.stagesOf': { en: (d: number, total: number) => `${d} / ${total} stages`, zh: (d: number, total: number) => `${d} / ${total} 阶段` },
  'prog.remaining': { en: (eta: string) => `${eta} left`, zh: (eta: string) => `剩余 ${eta}` },
  'prog.epQueued': { en: 'Queued', zh: '排队中' },
  'prog.epFailedAt': { en: (n: number, total: number) => `Failed at stage ${n}/${total}`, zh: (n: number, total: number) => `在第 ${n}/${total} 阶段失败` },
  'prog.epDone': { en: (dur: string) => `Done${dur ? `, ${dur}` : ''}`, zh: (dur: string) => `完成${dur ? `，耗时 ${dur}` : ''}` },
  'prog.epRunning': { en: (d: number, total: number, hint: string) => `${d} / ${total} stages${hint}`, zh: (d: number, total: number, hint: string) => `${d} / ${total} 阶段${hint}` },
  'prog.finalStages': { en: (d: number, total: number) => `${d}/${total} final stages`, zh: (d: number, total: number) => `${d}/${total} 全局阶段` },
  'prog.activityLog': { en: (n: number) => `Activity log (${n})`, zh: (n: number) => `活动日志（${n} 条）` },

  // ---- detail page chrome ----
  'detail.back': { en: 'Back to projects', zh: '返回项目列表' },
  'detail.projectId': { en: 'Project ID', zh: '项目 ID' },
  'detail.newMix': { en: 'New mix', zh: '新建混剪' },
  'detail.newMixTitle': { en: 'Generate a new mix', zh: '生成新混剪' },
  'detail.newMixDesc': { en: 'Describe the angle you want. We generate segment-selection proposals to pick from.', zh: '描述你想要的切入角度，系统会生成选片方案供你挑选。' },
  'detail.delete': { en: 'Delete', zh: '删除' },
  'toast.analysisDoneErr': { en: 'AI analysis finished with errors', zh: 'AI 分析完成，但存在错误' },
  'toast.analysisDone': { en: 'AI analysis complete', zh: 'AI 分析完成' },
  'toast.analysisDoneDesc': { en: 'Episodes analyzed. Story, Characters and Mix Plans tabs are now available.', zh: '剧集已分析完成。Story、Characters 和 Mix Plans 标签页现已可用。' },

  // ---- final cuts ----
  'cuts.empty': { en: 'No exported cuts yet. Go to the Edit tab, pick a render, and click “Export”.', zh: '还没有导出的成片。去「精修」标签页选一个 Render，点「导出成片」。' },
  'cuts.download': { en: 'Download', zh: '下载' },
  'cuts.sources': { en: (n: number) => `Sources (${n} clips)`, zh: (n: number) => `来源（${n} 个片段）` },
  'cuts.removed': { en: 'Removed from this project', zh: '已从本项目成片移除' },
  'cuts.removeHint': { en: 'Remove from this list (keeps the video in your Asset Library)', zh: '从成片列表移除（素材库中的视频仍保留）' },
  'cuts.removeConfirm': { en: 'Remove this cut from the project? The video stays in your Asset Library.', zh: '从本项目成片中移除这条？素材库中的视频仍会保留。' },
} satisfies Record<string, Entry>

export type DramaStringKey = keyof typeof S

export function dramaT(lang: DramaLang) {
  return (key: DramaStringKey, ...args: any[]): string => {
    const v = (S[key] as any)[lang]
    return typeof v === 'function' ? v(...args) : v
  }
}
